'use memo';

import {
  CrmAgencyConnectionDetailsResponseActiveProvider,
  useGetCrmAgencyConnectionDetails,
  useGetCrmAgentDetails,
  useInvalidateUseGetCrmAgentDetails,
  useUpdateCrmAgentDetails,
} from '@urbanx/agx.crm.client';
import {
  AgxBadge,
  AgxButton,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';
import { isEmailValid } from 'helpers/emailHelper';
import { useEffect, useState } from 'react';

interface AgentCrmRegistrationProps {
  agencyId: string;
  agentId: string;
}

const providerNames: Record<CrmAgencyConnectionDetailsResponseActiveProvider, string> = {
  [CrmAgencyConnectionDetailsResponseActiveProvider.Rex]: 'Rex',
  [CrmAgencyConnectionDetailsResponseActiveProvider.Agentbox]: 'Agentbox',
  [CrmAgencyConnectionDetailsResponseActiveProvider.BoxAndDice]: 'Box and Dice',
  [CrmAgencyConnectionDetailsResponseActiveProvider.Vault]: 'Vault',
};

const AgentCrmRegistration = ({ agencyId, agentId }: AgentCrmRegistrationProps) => {
  const { pushToast } = useToast();
  const { data: crmConnectionDetails, isPending: crmConnectionDetailsPending } =
    useGetCrmAgencyConnectionDetails(agencyId, Boolean(agencyId));
  const activeProvider = crmConnectionDetails?.activeProvider;
  const { data: registration, isPending: registrationPending } = useGetCrmAgentDetails(
    agencyId,
    agentId,
    Boolean(agencyId && agentId && activeProvider)
  );
  const [emailAddress, setEmailAddress] = useState('');
  const [registeredEmailAddress, setRegisteredEmailAddress] = useState('');
  const invalidateRegistration = useInvalidateUseGetCrmAgentDetails();

  useEffect(() => {
    setEmailAddress(registration?.externalUserEmailAddress ?? '');
    setRegisteredEmailAddress(registration?.externalUserEmailAddress ?? '');
  }, [registration?.externalUserEmailAddress]);

  const { updateCrmAgentDetails, updateCrmAgentDetailsPending } = useUpdateCrmAgentDetails({
    onSuccess: async (_, command) => {
      const savedEmailAddress = command.externalUserEmailAddress ?? '';
      setEmailAddress(savedEmailAddress);
      setRegisteredEmailAddress(savedEmailAddress);
      await invalidateRegistration(agencyId, agentId);
      pushToast({
        type: 'success',
        message: command.externalUserEmailAddress
          ? `${activeProvider ? providerNames[activeProvider] : 'CRM'} registration connected`
          : 'CRM registration disconnected',
      });
    },
    onError: (error) => {
      pushToast({
        type: 'error',
        message: error.message || 'Unable to update CRM registration.',
      });
    },
  });

  if (crmConnectionDetailsPending || (activeProvider && registrationPending)) {
    return (
      <Panel title="Agent CRM Registration" halfWidth>
        <></>
      </Panel>
    );
  }

  if (!activeProvider) {
    return (
      <Panel title="Agent CRM Registration" halfWidth>
        <AgxTypography id="agent-crm-not-connected" type="body_ui" size="medium">
          No CRM is connected to the Agency
        </AgxTypography>
      </Panel>
    );
  }

  const registered = Boolean(registeredEmailAddress);
  const requestPending = updateCrmAgentDetailsPending;

  const updateRegistration = (externalUserEmailAddress?: string) => {
    void updateCrmAgentDetails({ agencyId, agentId, externalUserEmailAddress }).catch(
      () => undefined
    );
  };

  return (
    <Panel
      title="Agent CRM Registration"
      halfWidth
      actionContent={
        <AgxBadge
          type="colourfill"
          size="sm"
          colour={registered ? 'green' : 'grey'}
          text={registered ? 'Registered' : 'Not registered'}
        />
      }
    >
      <AgxTextInput
        key={registeredEmailAddress}
        id="agentCrmEmailInput"
        type="email"
        label={`${providerNames[activeProvider]} email address`}
        onInputValueChange={({ value }) => setEmailAddress(value)}
        defaultValue={emailAddress}
        disabled={registered || requestPending}
        readonly={registered}
        stretch
        required
        hideOptionalText
      />
      <AgxSpacer direction="horizontal" fill contentJustification="end">
        {registered ? (
          <AgxButton
            id="unregister-agent-crm"
            text={requestPending ? 'Unregistering...' : 'Unregister'}
            size="medium"
            variant="hollow"
            danger
            disabled={requestPending}
            onClick={() => updateRegistration()}
          />
        ) : (
          <AgxButton
            id="register-agent-crm"
            text={requestPending ? 'Registering...' : 'Register'}
            size="medium"
            variant="primary"
            disabled={requestPending || !isEmailValid(emailAddress.trim())}
            onClick={() => updateRegistration(emailAddress.trim())}
          />
        )}
      </AgxSpacer>
    </Panel>
  );
};

export default AgentCrmRegistration;
